import { ArrowLeft, Bell, User } from 'react-feather';
import { Redirect, useHistory } from 'react-router-dom';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AddOrderStatus } from '__generated__/graphql';
import { CheckoutProps } from './CheckoutProvider';
import { CheckoutStep } from './helpers';
import { EmbedContext } from '../Common/EmbedProvider';
import { HeaderContainer } from '../Common/StickyHeader';
import CheckoutTimers, { CheckoutTimersContainer } from './CheckoutTimers';
import DetailsForm from './Details/DetailsForm';
import ExtrasForm from './Extras/ExtrasForm';
import FundraisingForm from './Fundraising/FundraisingForm';
import InvitationCodeForm from './InvitationCodeForm';
import Layout, { TitleBar } from '../Common/Layout';
import Pager from './Pager';
import PaymentForm from './Payment/PaymentForm';
import PersonalisationForm from './Personalisation/PersonalisationForm';
import Queue from './Queue';
import TeamForm from './Team/TeamForm';
import TicketsForm from './Tickets/TicketsForm';
import UI from '../../common/UI';
import useCheckoutUrlState from './useCheckoutUrlState';
import useDocumentTitle from '../../common/useDocumentTitle';
import useLocale from '../../common/useLocale';

const CheckoutPage = ({
  event,
  ticketCategories,
  checkoutSteps,
  activeStepIndex,
  nextRegistration,
  valid,
  completeCheckout,
  loading,
  error,
  status,
  preselectedCouponCode,
  preselectedCharity,
  queue,
}: CheckoutProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const history = useHistory();
  const { embedded } = useContext(EmbedContext);

  useDocumentTitle(event.title);

  const { scrollTop } = useContext(EmbedContext);

  const [{ paymentStatus }] = useCheckoutUrlState();

  const handleSubmit = () => {
    if (nextRegistration()) {
      scrollTop();
    } else if (activeStepIndex + 1 < checkoutSteps.length && valid[activeStepIndex]) {
      history.push(`/${event.id}/${checkoutSteps[activeStepIndex + 1]}`);
      scrollTop();
    } else if (valid[activeStepIndex]) {
      completeCheckout().catch(() => scrollTop());
    }

    return true;
  };

  useEffect(() => {
    // Scroll up to the error message
    if (status === AddOrderStatus.START_PAYMENT_FAILED) {
      scrollTop();
    }
  }, [status, scrollTop]);

  const activeStepName = checkoutSteps[activeStepIndex];

  const invitationCode = event.invitation_code;

  const intro = invitationCode?.invitation.description || event.description;

  const ticketsForSaleCount = ticketCategories.reduce(
    (sum, ticketCategory) => sum + ticketCategory.tickets_for_sale.length,
    0,
  );

  if (activeStepIndex === -1) {
    return <Redirect to={`/${event.id}`} />;
  }

  if (activeStepIndex > 0 && !valid[activeStepIndex - 1]) {
    return <Redirect to={`/${event.id}/${checkoutSteps[activeStepIndex - 1]}`} />;
  }

  return (
    <Layout event={event} embedded={embedded}>
      {!paymentStatus && intro && (
        <>
          <UI.HR />
          <UI.Div sc={{ color: 'gray.800' }}>
            <UI.HTML html={intro} />
          </UI.Div>
        </>
      )}

      {paymentStatus && (
        <UI.Div>
          {paymentStatus === 'pending' && (
            <UI.Warning>
              {t('common:payments.payment_pending')}
            </UI.Warning>
          )}
          {paymentStatus !== 'pending' && (
            <UI.Error>
              {t('common:payments.payment_failed')}
            </UI.Error>
          )}
        </UI.Div>
      )}

      {queue && (
        <Queue queue={queue} />
      )}

      {!queue && (
        <>
          <UI.Form id="CheckoutForm" name="checkout" onSubmit={() => handleSubmit()}>
            <TitleBar>
              <UI.Div>
                {activeStepIndex > 0 && (
                  <UI.FadeIn>
                    <UI.Link to={`/${event.id}/${checkoutSteps[activeStepIndex - 1]}`} title={t('back')}>
                      <UI.Icon>
                        <ArrowLeft />
                      </UI.Icon>
                    </UI.Link>
                  </UI.FadeIn>
                )}
              </UI.Div>
              <UI.Div style={{ minWidth: 0 }}>
                <UI.Truncate>
                  {(activeStepName === CheckoutStep.Tickets
                    && ticketCategories.length === 1 && ticketCategories[0]?.title
                  ) || t(`checkout_steps.${activeStepName}`)}
                </UI.Truncate>
              </UI.Div>
              {ticketsForSaleCount > 0 && (
                <UI.Div sc={{ small: true }}>
                  {activeStepIndex + 1}
                  {' / '}
                  {checkoutSteps.length}
                </UI.Div>
              )}
            </TitleBar>

            <CheckoutCard id="CheckoutContainer" sc={{ hasPanels: true, active: true }}>
              {ticketsForSaleCount > 0 && (
                <>
                  <CheckoutTimers isSticky={activeStepName === CheckoutStep.Tickets} />

                  {error && (
                    <UI.Div>
                      <UI.ServerErrors error={error} attributes={{ 'team.title': t('common:team_picker.team_name') }} />
                    </UI.Div>
                  )}

                  {status === AddOrderStatus.START_PAYMENT_FAILED && (
                    <UI.Div>
                      <UI.Error>
                        <UI.Strong sc={{ block: true }}>
                          {t('common:payments.could_not_start_payment')}
                        </UI.Strong>
                        {t('common:payments.try_again_in_a_minute')}
                      </UI.Error>
                    </UI.Div>
                  )}

                  {activeStepName === CheckoutStep.Tickets && (
                    <TicketsForm
                      preselectedCharity={preselectedCharity}
                      preselectedCouponCode={preselectedCouponCode}
                    />
                  )}
                  {activeStepName === CheckoutStep.Personalisation && (
                    <PersonalisationForm />
                  )}
                  {activeStepName === CheckoutStep.Extras && (
                    <ExtrasForm />
                  )}
                  {activeStepName === CheckoutStep.Team && (
                    <TeamForm />
                  )}
                  {activeStepName === CheckoutStep.Fundraising && (
                    <FundraisingForm />
                  )}
                  {activeStepName === CheckoutStep.Details && (
                    <DetailsForm />
                  )}
                  {activeStepName === CheckoutStep.Payment && (
                    <PaymentForm />
                  )}

                  <Pager
                    checkoutSteps={checkoutSteps}
                    activeStepIndex={activeStepIndex}
                    loading={loading}
                    key={activeStepName}
                  />
                </>
              )}

              {ticketsForSaleCount === 0 && (
                <UI.FadeIn sc={{ textAlign: 'center' }}>
                  <UI.Legend>
                    {t('no_tickets_available')}
                  </UI.Legend>
                  {event.enable_interest_list && (
                    <UI.Div sc={{ mt: 4 }}>
                      <UI.LinkButton sc={{ brand: 'secondary' }} to={`/${event.id}/interest`}>
                        <UI.Icon>
                          <Bell />
                        </UI.Icon>
                        {' '}
                        {t('interest_list.receive_updates')}
                      </UI.LinkButton>
                    </UI.Div>
                  )}
                </UI.FadeIn>
              )}
            </CheckoutCard>
          </UI.Form>

          <UI.GridContainer sc={{ gutter: 0.25 }}>
            {event.has_invitation_codes && (
              <InvitationCodeForm />
            )}

            {event.enable_public_resale && (
              <UI.LinkButton
                to={`/${event.id}/resale`}
                sc={{ blank: true, color: 'link', block: true }}
              >
                <UI.Icon>
                  <UI.Icons.Resale />
                </UI.Icon>
                {' '}
                {t('ticket_resale')}
                {event.cached_registrations_for_sale_count > 0 && ` (${formatNumber(event.cached_registrations_for_sale_count)})`}
              </UI.LinkButton>
            )}

            {event.enable_interest_list && (
              <UI.LinkButton
                to={`/${event.id}/interest`}
                sc={{ blank: true, color: 'link', block: true }}
              >
                <UI.Icon>
                  <Bell />
                </UI.Icon>
                {' '}
                {t('interest_list.receive_updates')}
              </UI.LinkButton>
            )}

            <UI.AButton
              href="/me"
              target="_blank"
              rel="noopener noreferrer"
              sc={{ blank: true, color: 'link', block: true }}
            >
              <UI.Icon>
                <User />
              </UI.Icon>
              {' '}
              {t('manage_registration')}
            </UI.AButton>
          </UI.GridContainer>
        </>
      )}
    </Layout>
  );
};

export const CheckoutCard = styled(UI.Card)`
  ${CheckoutTimersContainer} + * {
    border-top: 0 !important;
  }

  ${CheckoutTimersContainer} + ${UI.Div} ${HeaderContainer} {
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
  }
`;

export default CheckoutPage;
